import { useState, useEffect } from "react";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Car,
  DollarSign,
  TrendingUp,
  Calendar,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
} from "lucide-react";
import { format } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

export default function HostAnalyticsPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? enUS : es;
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    fetchAnalytics();
  }, []);

  useEffect(() => {
    fetchEarnings();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get("/analytics/host");
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await api.get(`/analytics/host/earnings?period=${period}`);
      setEarnings(response.data);
    } catch (error) {
      console.error("Error fetching earnings:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#111111]" />
      </div>
    );
  }

  const summary = analytics?.summary || {};

  const statCards = [
    {
      label: t("hostAnalytics.totalRevenue"),
      value: `$${summary.total_revenue?.toLocaleString() || 0}`,
      subtext: "MXN",
      icon: DollarSign,
      trend: "+12%",
      trendUp: true,
    },
    {
      label: t("hostAnalytics.completedTrips"),
      value: summary.completed_trips || 0,
      subtext: t("hostAnalytics.thisPeriod"),
      icon: Car,
      trend: "+8%",
      trendUp: true,
    },
    {
      label: t("hostAnalytics.occupancyRate"),
      value: `${summary.occupancy_rate || 0}%`,
      subtext: t("hostAnalytics.ofAvailability"),
      icon: Calendar,
      trend: "+5%",
      trendUp: true,
    },
    {
      label: t("hostAnalytics.avgValue"),
      value: `$${summary.avg_booking_value?.toLocaleString() || 0}`,
      subtext: t("hostAnalytics.perBooking"),
      icon: TrendingUp,
      trend: "+3%",
      trendUp: true,
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="host-analytics-page">
      <div className="container-main py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#111111]">
              Analytics
            </h1>
            <p className="text-neutral-500">{t("hostAnalytics.subtitle")}</p>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">{t("hostAnalytics.lastWeek")}</SelectItem>
              <SelectItem value="month">{t("hostAnalytics.lastMonth")}</SelectItem>
              <SelectItem value="year">{t("hostAnalytics.lastYear")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <div
              key={index}
              className="analytics-card"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <stat.icon className="w-5 h-5 text-[#111111]" />
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  stat.trendUp ? "text-emerald-600" : "text-red-500"
                }`}>
                  {stat.trendUp ? (
                    <ArrowUpRight className="w-4 h-4" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4" />
                  )}
                  {stat.trend}
                </div>
              </div>
              <p className="text-2xl font-heading font-bold text-[#111111] mb-1">
                {stat.value}
              </p>
              <p className="text-sm text-neutral-500">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Earnings Card */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-neutral-200 p-6">
            <h3 className="font-heading font-semibold text-lg mb-6">{t("hostAnalytics.earningsSummary")}</h3>
            
            {earnings && (
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-emerald-50 rounded-xl">
                  <p className="text-sm text-emerald-600 mb-1">{t("hostAnalytics.grossEarnings")}</p>
                  <p className="text-2xl font-bold text-emerald-700">
                    ${earnings.gross_earnings?.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-4 bg-neutral-100 rounded-xl">
                  <p className="text-sm text-neutral-600 mb-1">{t("hostAnalytics.platformFees")}</p>
                  <p className="text-2xl font-bold text-neutral-700">
                    ${earnings.platform_fees?.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-4 bg-[#111111] rounded-xl">
                  <p className="text-sm text-neutral-400 mb-1">{t("hostAnalytics.netEarnings")}</p>
                  <p className="text-2xl font-bold text-white">
                    ${earnings.net_earnings?.toLocaleString()}
                  </p>
                </div>
              </div>
            )}

            {/* Monthly Chart Placeholder */}
            <div className="mt-8">
              <h4 className="text-sm font-medium text-neutral-500 mb-4">{t("hostAnalytics.monthlyTrend")}</h4>
              <div className="flex items-end gap-2 h-40">
                {(analytics?.monthly_chart || []).map((month, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-[#111111] rounded-t-lg transition-all hover:bg-neutral-700"
                      style={{
                        height: `${Math.max(10, (month.revenue / (Math.max(...(analytics?.monthly_chart || []).map(m => m.revenue)) || 1)) * 100)}%`
                      }}
                    />
                    <p className="text-xs text-neutral-500 mt-2">
                      {month.month?.slice(5)}
                    </p>
                  </div>
                ))}
                {(!analytics?.monthly_chart || analytics.monthly_chart.length === 0) && (
                  <div className="flex-1 text-center text-neutral-400 py-10">
                    {t("hostAnalytics.noData")}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vehicle Performance */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-6">
            <h3 className="font-heading font-semibold text-lg mb-6">{t("hostAnalytics.vehiclePerformance")}</h3>
            
            <div className="space-y-4">
              {(analytics?.vehicle_performance || []).map((vehicle, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-[#FAFAFA] rounded-xl">
                  <img
                    src={vehicle.photo || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=100"}
                    alt={vehicle.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{vehicle.name}</p>
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <span>{vehicle.total_bookings} {t("hostAnalytics.trips")}</span>
                      {vehicle.rating > 0 && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                            {vehicle.rating.toFixed(1)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">${vehicle.revenue?.toLocaleString()}</p>
                    <p className="text-xs text-neutral-500">MXN</p>
                  </div>
                </div>
              ))}
              
              {(!analytics?.vehicle_performance || analytics.vehicle_performance.length === 0) && (
                <div className="text-center py-8 text-neutral-400">
                  <Car className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t("hostAnalytics.noCarsYet")}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="mt-6 bg-white rounded-2xl border border-neutral-200 p-6">
          <h3 className="font-heading font-semibold text-lg mb-6">{t("hostAnalytics.recentBookings")}</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left py-3 px-4 text-xs font-medium text-neutral-500 uppercase tracking-wider">ID</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-neutral-500 uppercase tracking-wider">{t("hostAnalytics.dates")}</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-neutral-500 uppercase tracking-wider">{t("hostAnalytics.status")}</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-neutral-500 uppercase tracking-wider">{t("hostAnalytics.total")}</th>
                </tr>
              </thead>
              <tbody>
                {(analytics?.recent_bookings || []).slice(0, 5).map((booking) => (
                  <tr key={booking.booking_id} className="border-b border-neutral-50 hover:bg-neutral-50">
                    <td className="py-3 px-4 text-sm font-mono">{booking.booking_id?.slice(0, 12)}...</td>
                    <td className="py-3 px-4 text-sm text-neutral-600">
                      {format(new Date(booking.pickup_date), "dd MMM", { locale: dateLocale })} - {format(new Date(booking.return_date), "dd MMM", { locale: dateLocale })}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        booking.status === "completed" ? "status-completed" :
                        booking.status === "active" ? "status-active" :
                        booking.status === "confirmed" ? "status-confirmed" :
                        booking.status === "canceled" ? "status-canceled" :
                        "status-pending"
                      }`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm font-semibold text-right">${booking.total_price?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {(!analytics?.recent_bookings || analytics.recent_bookings.length === 0) && (
              <div className="text-center py-8 text-neutral-400">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t("hostAnalytics.noBookingsYet")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
